import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { API, TagDTO } from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { ErrorService } from '../../../services/error.service';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { RequestClass } from '../../../services/global/RequestClass';

type SplitButtonAction = 'edit';

@Component({
  selector: 'app-tags',
  templateUrl: './tags.component.html',
  styleUrls: ['./tags.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagsComponent extends RequestClass {
  public readonly util = inject(UtilService);
  public readonly permissionService = inject(PermissionService);
  private readonly errorHandler = inject(ErrorService);
  private readonly api = inject(API);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // table wrapper variables
  tags: TagDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .shared_Tag_getTags(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (tags: TagDTO[]) => {
          this.tags = this.buildSplitButtons(tags);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.tag_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.tagEdit,
          pathParams: [{ id: 'tagID', value: item?.id }],
        });
      }
    });
    return result;
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
